import React, { useState, useEffect } from 'react';
import { 
  Settings, Plus, Trash2, Save, Copy, Download, Upload, 
  ArrowRight, AlertCircle, CheckCircle, X, Move, Coffee, 
  Clock, BookOpen, FileText, Shuffle, Eye, EyeOff,
  ChevronsLeft
} from 'lucide-react';
import { supabase } from '@/src/supabase/client';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Database } from '@/database.types';
import { showNotification } from '@mantine/notifications';
import { formatDate } from '@/src/utils';

export const AdminTestConfig = () => {
  // Available categories for dropdowns
  const availableCategories = [
    "Logical Reasoning",
    "Reading Comprehension", 
    "Analytical Reasoning",
    "Writing Sample"
  ];
  
  // Initial state based on the provided JSON structure
  const initialState: any = {
    // shuffle: true,
    // sections: [
    //   {type: "section", category: "Logical Reasoning"},
    //   {type: "section", category: "Logical Reasoning"},
    //   {type: "section", category: "Reading Comprehension"},
    //   {
    //     type: "mix", 
    //     sections: [
    //       {type: "section", category: "Logical Reasoning"},
    //       {type: "section", category: "Reading Comprehension"}
    //     ]
    //   }
    // ],
    // sequence: [
    //   {type: "section", duration: 35},
    //   {type: "section", duration: 35},
    //   {type: "break", duration: 10},
    //   {type: "section", duration: 35},
    //   {type: "break", duration: 10},
    //   {type: "section", duration: 35}
    // ],
    unscored: {
      filter: [],
      quantity: 1
    }
  };
  
  const { id } = useParams();
  const navigate = useNavigate();

  // State for the test configuration
  const [config, setConfig] = useState(initialState);

  // State for JSON export/import
  const [jsonView, setJsonView] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [notification, setNotification] = useState(null);
  
  // Generate JSON text from config state for display and export
  useEffect(() => {
    setJsonText(JSON.stringify(config, null, 2));
  }, [config]);

  const { isLoading } = useQuery({
    queryKey: ['AdminPage', { component: 'Test Configuration', id }],
    queryFn: async () => {
      if (id) {
        const { data, error } = await supabase.from('test_schema').select('*').eq('id', id).single();
        if (error) {
          showNotification('error', `Error loading schema: ${error.message}`);
        }

        if (data) {
          setConfig(data.sections);
        }

        return data;
      }
      
      return undefined;
    },
    enabled: !!id && !config.sections,
  });
  
  // Import JSON configuration
  const importConfig = () => {
    try {
      const newConfig = JSON.parse(jsonText);
      // Basic validation
      if (!newConfig.sections || !newConfig.sequence || !newConfig.unscored) {
        throw new Error("Invalid configuration: Missing required fields");
      }
      setConfig(newConfig);
      return newConfig;
    } catch (error) {
      throw error;
    }
  };

  const saveConfig = async () => {
    try {
      const cfg = importConfig();
      if (!id) {
        const { data, error } = await supabase.from('test_schema').insert({
          test_packet: 'LSAT', // TODO: make this dynamic
          sections: cfg,
        }).select("*").single();

        if (error) {
          throw error;
        }

        showNotification("success", "Configuration imported successfully");

        if (data) {
          navigate(`/admin/tests/${data.id}`);
        }
      } else {
        const { error } = await supabase.from('test_schema').update({
          sections: cfg,
        }).eq('id', id);

        if (error) {
          throw error;
        }

        showNotification("success", "Configuration imported successfully");
      }
    } catch (error) {
      if (error) {
        showNotification('error', `Error updating test schema: ${error.message}`);
      }
    }
  }
  
  // Export config as JSON file
  const exportConfig = () => {
    const blob = new Blob([jsonText], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lsat-test-config.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showNotification("success", "Configuration exported to file");
  };
  
  // Copy JSON to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonText);
    showNotification("success", "Configuration copied to clipboard");
  };
  
  // Show notification with auto-dismiss
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };
  
  // Toggle shuffle setting
  const toggleShuffle = () => {
    setConfig(prev => ({
      ...prev,
      shuffle: !prev.shuffle
    }));
  };
  
  // Add a new section
  const addSection = () => {
    setConfig(prev => ({
      ...prev,
      sections: (prev?.sections ?? []).concat([
        {type: "section", category: availableCategories[0]}
      ])
    }));
  };
  
  const addBreak = () => {
    setConfig(prev => ({
      ...prev,
      sections: (prev?.sections ?? []).concat([
        {type: "break", category: availableCategories[0]}
      ])
    }));
  };
  

  // Add a new mixed section
  const addMixedSection = () => {
    setConfig(prev => ({
      ...prev,
      sections: (prev?.sections ?? []).concat([
        {
          type: "mix", 
          sections: [
            {type: "section", category: availableCategories[0]}
          ]
        }
      ])
    }));
  };
  
  // Remove a section
  const removeSection = (index) => {
    setConfig(prev => {
      const newSections = [...prev.sections];
      newSections.splice(index, 1);
      return {
        ...prev,
        sections: newSections
      };
    });
  };
  
  // Update a section
  const updateSection = (index, key, value) => {
    setConfig(prev => {
      const newSections = [...prev.sections];
      newSections[index] = {
        ...newSections[index],
        [key]: value
      };
      return {
        ...prev,
        sections: newSections
      };
    });
  };
  
  // Add a subsection to a mixed section
  const addSubsection = (parentIndex) => {
    setConfig(prev => {
      const newSections = [...prev.sections];
      newSections[parentIndex] = {
        ...newSections[parentIndex],
        sections: [
          ...newSections[parentIndex].sections,
          {type: "section", category: availableCategories[0]}
        ]
      };
      return {
        ...prev,
        sections: newSections
      };
    });
  };
  
  // Remove a subsection from a mixed section
  const removeSubsection = (parentIndex, subIndex) => {
    setConfig(prev => {
      const newSections = [...prev.sections];
      const newSubsections = [...newSections[parentIndex].sections];
      newSubsections.splice(subIndex, 1);
      newSections[parentIndex] = {
        ...newSections[parentIndex],
        sections: newSubsections
      };
      return {
        ...prev,
        sections: newSections
      };
    });
  };
  
  // Update a subsection
  const updateSubsection = (parentIndex, subIndex, key, value) => {
    setConfig(prev => {
      const newSections = [...prev.sections];
      const newSubsections = [...newSections[parentIndex].sections];
      newSubsections[subIndex] = {
        ...newSubsections[subIndex],
        [key]: value
      };
      newSections[parentIndex] = {
        ...newSections[parentIndex],
        sections: newSubsections
      };
      return {
        ...prev,
        sections: newSections
      };
    });
  };
  
  // Add a sequence item
  const addSequenceItem = (type) => {
    setConfig(prev => ({
      ...prev,
      sequence: (prev.sequence ?? []).concat([
        {type, duration: type === "break" ? 10 : 35}
      ])
    }));
  };
  
  // Remove a sequence item
  const removeSequenceItem = (index) => {
    setConfig(prev => {
      const newSequence = [...prev.sequence];
      newSequence.splice(index, 1);
      return {
        ...prev,
        sequence: newSequence
      };
    });
  };
  
  // Update a sequence item
  const updateSequenceItem = (index, key, value) => {
    setConfig(prev => {
      const newSequence = [...prev.sequence];
      newSequence[index] = {
        ...newSequence[index],
        [key]: value
      };
      return {
        ...prev,
        sequence: newSequence
      };
    });
  };
  
  // Toggle a category in the unscored filter
  const toggleUnscoredCategory = (category) => {
    setConfig(prev => {
      const newFilter = prev.unscored?.filter?.includes(category)
        ? prev.unscored?.filter?.filter(cat => cat !== category)
        : [...prev.unscored?.filter ?? [], category];
      
      return {
        ...prev,
        unscored: {
          ...prev.unscored,
          filter: newFilter
        }
      };
    });
  };
  
  // Update unscored quantity
  const updateUnscoredQuantity = (quantity) => {
    setConfig(prev => ({
      ...prev,
      unscored: {
        ...prev.unscored,
        quantity: parseInt(quantity) || 0
      }
    }));
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Settings className="h-6 w-6 text-indigo-600 mr-2" />
              <h1 className="text-xl font-bold text-gray-900">LSAT Test Configuration</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/admin/tests')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ChevronsLeft className="h-4 w-4 mr-1" />
                Back to Tests
              </button>

              {/* <button 
                onClick={() => setJsonView(!jsonView)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {jsonView ? <Eye className="h-4 w-4 mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
                {jsonView ? "View Form" : "View JSON"}
              </button> */}
              
              {/* <button 
                onClick={copyToClipboard}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy JSON
              </button>
              
              <button 
                onClick={exportConfig}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Download className="h-4 w-4 mr-1" />
                Export
              </button> */}
            </div>
          </div>
        </div>
      </header>
      
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg flex items-start justify-between ${
          notification.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          <div className="flex">
            {notification.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            <p>{notification.message}</p>
          </div>
          <button 
            onClick={() => setNotification(null)}
            className="text-gray-400 hover:text-gray-500 ml-4"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
      
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {isLoading &&
            <div className="bg-white shadow rounded-lg p-6">
              <div className="mb-4 flex justify-between items-center">
                Loading test...
              </div>
            </div>
          }
          {jsonView ? (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="mb-4 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">JSON Configuration</h2>
                <button 
                  onClick={importConfig}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Import
                </button>
              </div>
              <textarea
                className="w-full h-96 p-4 font-mono text-sm border rounded-md"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
              />
            </div>
          ) : (
            <div className="space-y-6">
              {/* General Settings */}
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">General Settings</h2>
                <div className="flex items-center">
                  <div className="mr-8">
                    <div className="relative inline-block w-10 mr-2 align-middle select-none">
                      <input
                        type="checkbox"
                        id="shuffle-toggle"
                        className="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        style={{
                          top: "0px",
                          left: config.shuffle ? "15px" : "0px",
                          borderColor: config.shuffle ? "#4f46e5" : "#d1d5db",
                          backgroundColor: config.shuffle ? "#4f46e5" : "white",
                          transition: "all 0.2s ease"
                        }}
                        checked={config.shuffle}
                        onChange={toggleShuffle}
                      />
                      <label
                        htmlFor="shuffle-toggle"
                        className="block overflow-hidden h-6 rounded-full bg-gray-200 cursor-pointer"
                      />
                    </div>
                    <label htmlFor="shuffle-toggle" className="ml-2 text-sm text-gray-700">
                      Shuffle Sections
                    </label>
                  </div>
                </div>
              </div>
              
              {/* Sections Configuration */}
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Sections Configuration</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={addSection}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Section
                    </button>
                    <button
                      onClick={addMixedSection}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Mixed Section
                    </button>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {config.sections?.map((section, index) => (
                    <div key={`section-${index}`} className="border rounded-md p-4 bg-gray-50">
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center">
                          <Move className="h-5 w-5 text-gray-400 mr-2" />
                          <h3 className="text-md font-medium text-gray-900">
                            {section.type === "section" ? "Section" : "Mixed Section"} {index + 1}
                          </h3>
                        </div>
                        <button
                          onClick={() => removeSection(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                      
                      {section.type === "section" ? (
                        <div className="mb-3">
                          <label htmlFor={`section-${index}-category`} className="block text-sm font-medium text-gray-700 mb-1">
                            Category
                          </label>
                          <select
                            id={`section-${index}-category`}
                            value={section.category}
                            onChange={(e) => updateSection(index, "category", e.target.value)}
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          >
                            {availableCategories?.map(category => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-500">Subsections</span>
                            <button
                              onClick={() => addSubsection(index)}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Subsection
                            </button>
                          </div>
                          
                          <div className="pl-4 border-l-2 border-gray-200 space-y-3">
                            {section.sections?.map((subsection, subIndex) => (
                              <div key={`subsection-${index}-${subIndex}`} className="flex items-center justify-between">
                                <div className="flex-grow mr-3">
                                  <select
                                    value={subsection.category}
                                    onChange={(e) => updateSubsection(index, subIndex, "category", e.target.value)}
                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                  >
                                    {availableCategories?.map(category => (
                                      <option key={category} value={category}>{category}</option>
                                    ))}
                                  </select>
                                </div>
                                <button
                                  onClick={() => removeSubsection(index, subIndex)}
                                  className="text-red-500 hover:text-red-700"
                                  disabled={section.sections?.length <= 1}
                                >
                                  <Trash2 className={`h-4 w-4 ${section.sections.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Sequence Configuration */}
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Test Sequence</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => addSequenceItem("section")}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <BookOpen className="h-4 w-4 mr-1" />
                      Add Section
                    </button>
                    <button
                      onClick={() => addSequenceItem("break")}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <Coffee className="h-4 w-4 mr-1" />
                      Add Break
                    </button>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-start">
                  {config.sequence?.map((item, index) => (
                    <div key={`sequence-${index}`} className="mr-2 mb-4">
                      <div className={`border rounded-md p-3 ${item.type === "break" ? "bg-amber-50" : "bg-blue-50"}`}>
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center">
                            {item.type === "break" ? (
                              <Coffee className="h-4 w-4 text-amber-500 mr-1" />
                            ) : (
                              <BookOpen className="h-4 w-4 text-blue-500 mr-1" />
                            )}
                            <span className="text-sm font-medium">
                              {item.type === "break" ? "Break" : "Section"}
                            </span>
                          </div>
                          <button
                            onClick={() => removeSequenceItem(index)}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-gray-400 mr-1" />
                          <input
                            type="number"
                            min="1"
                            value={item.duration}
                            onChange={(e) => updateSequenceItem(index, "duration", parseInt(e.target.value) || 0)}
                            className="w-16 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          />
                          <span className="ml-1 text-sm text-gray-500">min</span>
                        </div>
                      </div>
                      
                      {index < config.sequence?.length - 1 && (
                        <div className="flex justify-center my-1">
                          <ArrowRight className="h-4 w-4 text-gray-400" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Unscored Configuration */}
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Unscored Section Configuration</h2>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categories that can be included in unscored sections:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableCategories?.map((category) => (
                      <button
                        key={category}
                        onClick={() => toggleUnscoredCategory(category)}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          config.unscored?.filter.includes(category)
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {category}
                        {config.unscored?.filter.includes(category) && (
                          <CheckCircle className="ml-1 h-4 w-4" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label htmlFor="unscored-quantity" className="block text-sm font-medium text-gray-700 mb-1">
                    Number of unscored sections:
                  </label>
                  <input
                    type="number"
                    id="unscored-quantity"
                    min="0"
                    value={config.unscored?.quantity}
                    onChange={(e) => updateUnscoredQuantity(e.target.value)}
                    className="w-20 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <button
                  onClick={() => saveConfig()}
                  className="mt-10 w-full text-white bg-linear-to-r from-indigo-500 to-purple-600 rounded-md text-center py-4 mv-4"
                >Update Settings</button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export function AdminTestPage() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['AdminPage', { component: 'tests' }],
    queryFn: async() => {
      const { data, error } = await supabase.from('test_schema').select('*').order('expires_on', {nullsFirst: true, ascending: false});
      if (error) {
        showNotification({
          message: error.message,
        });
      }

      return data;
    }
  });

  return (
    <div className="container mx-auto px-4 pb-12 mt-10">
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Table Header */}
      <div className="grid grid-cols-12 bg-gray-50 p-4 border-b border-gray-200 text-sm font-medium text-gray-500">
        <div className="col-span-4 md:col-span-3">Category</div>
        <div className="col-span-3 md:col-span-3">Expires On</div>
        <div className="hidden md:block md:col-span-3">Status</div>
        <div className="col-span-3 md:col-span-2 text-right">Actions</div>
      </div>

      {(data ?? []).length > 0 ? (
        data?.map((order) => (
          <div key={order.id} className="border-b border-gray-200 last:border-0">
            {/* Order Summary Row */}
            <div 
              className="grid grid-cols-12 p-4 items-center hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => navigate(`/admin/tests/${order.id}`)}
            >
              <div className="col-span-3 md:col-span-3 font-medium"><Link to={`/admin/tests/${order.id}`}>{order.test_packet}</Link></div>
              <div className="col-span-4 md:col-span-3 text-gray-500">{order.expires_on && formatDate(order.expires_on)}</div>
              <div className="hidden md:block md:col-span-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  !order.expires_on
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {`${!order.expires_on ? 'Active ' : 'Inactive'}`}
                </span>
              </div>
              <div className="col-span-3 md:col-span-2 text-right">

              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="p-8 text-center text-gray-500">
          <p>{isLoading ? 'Loading tests...' : 'No tests found'}</p>
        </div>
      )}
    </div>
  </div>
  )
}
